import {
  CSSProperties,
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import { Autocomplete } from '@mui/material';
import { DropDownMenuItem } from '../DropdownMenu';
import styles from './sharedAutoComplete.module.css';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-props-no-spreading */
export interface SharedAutoCompleteProps<T=any> {
  value?: any
  options: DropDownMenuItem<T>[]
  disabled?: boolean
  onSelect: (value: any) => void
  onDeselect?: () => void
  onTextInput?: (value: string) => void
  onBlur?: () => void
  onBlurSetTextAsSelected?: boolean
  popperZIndex?: number
  disablePortal?: boolean
  searchIcon?: boolean
  placeholder?: string
  style?: CSSProperties
  menuStyle?: CSSProperties
  closeOnExternalScroll?: boolean
  inputReadOnly?: boolean
  autoSetValueOnSelect?: boolean
}

export interface SharedAutoCompleteRef {
  getValue: () => any
}

export default forwardRef<SharedAutoCompleteRef, SharedAutoCompleteProps>((props, ref) => {
  const { onBlurSetTextAsSelected = true, closeOnExternalScroll = true, autoSetValueOnSelect = true } = props;
  const [text, setText] = useState<any>(props.value ?? '');
  const [inputValue, setInputValue] = useState<string>('');
  const [open, setOpen] = useState(false);
  const textRef = useRef<any>(props.value);
  const refInput = useRef<HTMLInputElement>(null);

  const handleTextInput = (value: string) => {
    if (autoSetValueOnSelect) {
      setInputValue(value);
      setText(value);
    }
    if (props.onTextInput) {
      props.onTextInput(value);
    }
  };

  const handleSelect = useCallback((selected: any) => {
    if (!selected.value && props.onDeselect) {
      props.onDeselect();
      return;
    }
    props.onSelect(selected);
  }, [props]);

  const handleBlur = useCallback(() => {
    if (props.onBlur) {
      props.onBlur();
    }
    if (onBlurSetTextAsSelected && inputValue !== textRef.current) {
      setText(textRef.current);
      setInputValue(textRef.current);
    }
  }, [props, inputValue, onBlurSetTextAsSelected]);

  useImperativeHandle(ref, () => ({
    getValue() {
      return textRef.current;
    },
  }));

  useEffect(() => {
    if (!open || !closeOnExternalScroll) {
      return () => {};
    }

    const handleScroll = ({ target }: Event) => {
      if (
        target instanceof Element
        && target.closest('.shared-autocomplete-listbox')
      ) {
        return;
      }

      setOpen(false);
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open, closeOnExternalScroll]);

  useEffect(() => {
    refInput.current?.focus();
    refInput.current?.click();
  }, []);

  useEffect(() => {
    setText(props.value || '');
    textRef.current = props.value || '';
    setInputValue(props.value || '');
  }, [props.value]);

  return (
    <div style={props.style}>
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        freeSolo
        disablePortal={props.disablePortal}
        blurOnSelect
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.text || '')}
        isOptionEqualToValue={(option, value) => option === value}
        value={text}
        onChange={(_event: any, newValue: any) => {
          if (newValue == null) return;
          if (autoSetValueOnSelect) {
            setText(newValue.text);
            textRef.current = newValue.text;
          }
          handleSelect(newValue);
        }}
        onBlurCapture={handleBlur}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue) => {
          handleTextInput(newInputValue);
        }}
        id="controllable-states-demo"
        options={props.options}
        sx={{
          width: '100%',
        }}
        renderOption={(optionProps, option) => (
          <li {...optionProps} title={option.text}>
            {option.text}
          </li>
        )}
        renderInput={(params) => (
          <div ref={params.InputProps.ref} className={styles.inputContainer}>
            {props.searchIcon && <span className={styles.searchIcon}>&#xF002;</span>}
            <input
              type="text"
              {...params.inputProps}
              readOnly={props.inputReadOnly}
              placeholder={props.placeholder}
              style={{ width: '100%' }}
              className={[styles.AutoCompleteInputStyle, props.disabled ? styles.disabled : ''].join(' ')}
              title={text ? String(text) : undefined}
            />
          </div>
        )}
        componentsProps={{
          popper: {
            sx: {
              zIndex: props.popperZIndex ? props.popperZIndex : undefined,
              '& .MuiAutocomplete-listbox': {
                margin: 0,
                padding: 0,
              },
              '& .MuiAutocomplete-option': {
                display: 'block !important',
                fontFamily: "'Noto Sans JP', 'Hiragino Sans', sans-serif !important",
                minHeight: '40px !important',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                alignContent: 'center',
                margin: 0,
                '&:hover': {
                  backgroundColor: '#D2D2D2 !important',
                },
                '&:not(:hover)': {
                  backgroundColor: 'transparent !important',
                },
              },
            },
          },
          paper: {
            sx: {
              fontSize: '14px',
            },
          },
        }}
        ListboxProps={{
          className: 'shared-autocomplete-listbox',
          style: props.menuStyle,
        }}
        disabled={props.disabled}
      />
    </div>
  );
});
